/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx, css } from '@emotion/react'
import { PureComponent } from 'react'
import { FormattedDate, FormattedMessage } from 'react-intl'
import { capitalize } from 'lodash'

import type { UseEuiTheme, WithEuiThemeProps } from '@elastic/eui'
import { EuiText, EuiHealth, withEuiTheme } from '@elastic/eui'

import type { LineItem } from '@modules/cloud-api/v1/types'
import { CuiTable } from '@modules/cui/Table'
import CuiElasticConsumptionUnits from '@modules/cui/formatters/CuiElasticConsumptionUnits'
import type { CuiTableColumn } from '@modules/cui/Table'
import { isActiveItem, isExpiredItem, isFutureItem } from '@modules/billing-lib/utils'

import { numericDateTime } from '@/config/dates'

import DetailsGridPopover from '../DetailsGridPopover'

import type { FilterState } from '../types'
import type { ReactElement } from 'react'

interface Props {
  prepaidBalanceLineItems: LineItem[]
  initialLoading?: boolean
  filter: FilterState
  rows: LineItem[]
}

const prepaidTableCss = (theme: UseEuiTheme) => css`
  .inactive-line-item {
    .euiTableRowCell {
      color: ${theme.euiTheme.colors.textDisabled};
    }
  }
`

class PrepaidsTable extends PureComponent<Props & WithEuiThemeProps> {
  render(): ReactElement {
    const { initialLoading, rows, filter, theme } = this.props
    const gridColumns = this.getGridColumns()

    if (!initialLoading && rows.length === 0) {
      return (
        <EuiText size='s'>
          <FormattedMessage
            data-test-id={`empty-state-filter-${filter}`}
            id='billing-details-summary.empty-grid.no-active-balances'
            defaultMessage='<strong>You have no {filter} balances.</strong> <capitalize>{filter}</capitalize> balances will show up here.'
            values={{
              filter,
              capitalize: (content: string) => capitalize(content),
              strong: (content) => <strong>{content}</strong>,
            }}
          />
        </EuiText>
      )
    }

    return (
      <div css={prepaidTableCss(theme)}>
        <CuiTable<LineItem>
          getRowId={(item) => item.id}
          getRowTestSubj={(item) => `table-line-item-${item.id}`}
          initialLoading={initialLoading}
          rows={rows}
          columns={gridColumns.items}
          rowClass={this.getRowClass}
          initialSort={gridColumns.initialSort}
          initialSortDirection='asc'
        />
      </div>
    )
  }

  renderBalanceColumn(value: number): ReactElement {
    return (
      <EuiText size='s'>
        <CuiElasticConsumptionUnits unit='none' value={value} />
      </EuiText>
    )
  }

  getRowClass = (lineItem: LineItem): string => {
    const inactiveClass = 'inactive-line-item'

    return !isActiveItem(lineItem, new Date()) ? inactiveClass : ''
  }

  getStatusCell = (props: Pick<LineItem, 'start' | 'end'>) => {
    const date = new Date()
    const { theme } = this.props

    if (isActiveItem(props, date)) {
      return (
        <EuiHealth color={theme.euiTheme.colors.success}>
          <FormattedMessage id='billing-details-summary.active-label' defaultMessage='Active' />
        </EuiHealth>
      )
    }

    if (isExpiredItem(props, date)) {
      return (
        <EuiHealth color={theme.euiTheme.colors.mediumShade}>
          <FormattedMessage id='billing-details-summary.expired-label' defaultMessage='Expired' />
        </EuiHealth>
      )
    }

    if (isFutureItem(props, date)) {
      return (
        <EuiHealth color={theme.euiTheme.colors.warning}>
          <FormattedMessage id='billing-details-summary.future-label' defaultMessage='Future' />
        </EuiHealth>
      )
    }

    return
  }

  getGridColumns(): {
    initialSort: CuiTableColumn<LineItem>
    items: Array<CuiTableColumn<LineItem>>
  } {
    const expirationDateColumn: CuiTableColumn<LineItem> = {
      label: (
        <FormattedMessage
          id='prepaid-account-details.expiration-date'
          defaultMessage='Expiration date'
        />
      ),
      render: ({ end }) => (
        <FormattedDate
          data-test-id='expiration-date-column-value'
          value={end}
          {...numericDateTime}
        />
      ),
      sortKey: ({ end }) => new Date(end),
      width: '250px',
    }

    return {
      initialSort: expirationDateColumn,
      items: [
        {
          label: <FormattedMessage id='prepaid-account-details.status' defaultMessage='Status' />,
          render: ({ start, end }) => this.getStatusCell({ start, end }),
          width: '90px',
        },
        {
          label: (
            <FormattedMessage
              id='prepaid-account-details.initial-balance'
              defaultMessage='Credits'
            />
          ),
          render: ({ ecu_quantity }) => this.renderBalanceColumn(ecu_quantity),
          align: 'right',
        },
        {
          label: (
            <FormattedMessage id='prepaid-account-details.amount-spent' defaultMessage='Used' />
          ),
          render: ({ ecu_balance, ecu_quantity }) =>
            this.renderBalanceColumn(ecu_quantity - ecu_balance),
          align: 'right',
        },
        {
          label: (
            <FormattedMessage
              id='prepaid-account-details.remaining-balance'
              defaultMessage='Remaining balance'
            />
          ),
          render: ({ ecu_balance }) => this.renderBalanceColumn(ecu_balance),
          align: 'right',
        },
        expirationDateColumn,
        {
          label: (
            <FormattedMessage
              id='prepaid-account-details.contract-info'
              defaultMessage='Contract info'
            />
          ),
          render: (lineItem) => <DetailsGridPopover details={lineItem} />,
        },
      ],
    }
  }
}

export default withEuiTheme(PrepaidsTable)
